import React, { useEffect, useState, createElement } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { AffiliateTerms } from './pages/AffiliateTerms';
import { Privacy } from './pages/Privacy';
import { VulnerabilityDisclosure } from './pages/VulnerabilityDisclosure';
import { Contact } from './pages/Contact';
import { Support } from './pages/Support';
import { Careers } from './pages/Careers';
import { Enterprise } from './pages/Enterprise';
import { EnterprisePlatform } from './pages/EnterprisePlatform';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { TrialFlow } from './pages/TrialFlow';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { Integrations } from './pages/Integrations';
import { IntegrationDetail } from './pages/IntegrationDetail';
import { Bespoke } from './pages/Bespoke';
import { Consulting } from './pages/Consulting';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { GigaForChatGPT } from './pages/GigaForChatGPT';
import { GigaForChatGPT2 } from './pages/GigaForChatGPT2';
import { CompanyBrain } from './pages/CompanyBrain';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Agents } from './pages/Agents';
import { Home } from './pages/Home';
import { HomeAlternative } from './pages/HomeAlternative';
import { HomeArchitecture } from './pages/HomeArchitecture';
import { EcommerceHome } from './pages/EcommerceHome';
import { FutureHome } from './pages/FutureHome';
import { WorkspaceHome } from './pages/WorkspaceHome';
import { HomeTeamWorkspace } from './pages/HomeTeamWorkspace';
import { ReferralCapture } from './pages/ReferralCapture';
import { Dashboard } from './pages/Dashboard';
import { WorkflowShow } from './pages/WorkflowShow';
import { OnboardingFunnel } from './pages/OnboardingFunnel';
import { WorkspaceAlreadyConnected } from './pages/WorkspaceAlreadyConnected';
import { SphereDirections } from './pages/SphereDirections';
import { ChatTransition } from './pages/ChatTransition';
import { UseCasesConcepts } from './pages/UseCasesConcepts';
import { FontOptions } from './pages/FontOptions';
import { ClaudeFont } from './pages/ClaudeFont';
import { CreatorProgram } from './pages/CreatorProgram';
import { ViktorAlternative } from './pages/ViktorAlternative';
import { FeatureDetail } from './pages/FeatureDetail';
import { AIAgents } from './pages/AIAgents';
import { SignIn } from './pages/SignIn';
import { Developers } from './pages/Developers';
import { NewInfrastructure } from './pages/NewInfrastructure';
import { Alt } from './pages/Alt';
import { ExportDashboards } from './pages/ExportDashboards';
import { CreatorPack } from './pages/CreatorPack';
import { CopyModeProvider } from './components/copy/copyMode';
import { resolveRouteMeta, isNoindexRoute } from './lib/routeMeta';
/**
 * Scrolls the window to the top on every route change.
 */
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
/**
 * Sets <title> and <meta name="description"> per route.
 * Gets baked into each static HTML file by the prerenderer.
 */
function PageMeta() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    // Titles/descriptions live in src/lib/routeMeta.ts so the build-time
    // prerender can bake the SAME values into each static HTML file.
    const {
      title,
      desc
    } = resolveRouteMeta(pathname);
    document.title = title;
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalHref = `https://getgiga.com${pathname === '/' ? '/' : pathname}`;
    if (!isNoindexRoute(pathname)) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalHref);
    } else if (canonical) {
      canonical.remove();
    }
    const meta = document.querySelector('meta[name="description"]');
    if (desc) {
      if (meta) {
        meta.setAttribute('content', desc);
      } else {
        const el = document.createElement('meta');
        el.name = 'description';
        el.content = desc;
        document.head.appendChild(el);
      }
    }
    // Keep search engines out of the /unlisted and /app-screen-mockups
    // namespaces. This mirrors the noindex meta baked into the prerendered
    // HTML (see prerender.mjs) so it's correct on client-side navigation too.
    let robots = document.querySelector('meta[name="robots"]');
    if (isNoindexRoute(pathname)) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      // Returning to a public route — remove the restrictive directive.
      robots.remove();
    }
  }, [pathname]);
  return null;
}
export function App() {
  return <CopyModeProvider data-id="element-0" data-mp="33gefd7wv1xmw">
      <ScrollToTop data-id="element-1" data-mp="33hz8ct3j18in" />
      <PageMeta data-id="element-2" data-mp="33h7epnthpkv0" />
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-3" data-mp="2yj5v91s5jjj0" />
      <Routes data-id="element-4" data-mp="3i3wbvj09rly8">
        {/* ──────────────────────────────────────────────────────────────
         APP SCREEN MOCKUPS — product/app UI mockups the marketing team uses
         for reviews. These are NOT the live product (the real signup/login
         lives on app.getgiga.com). Grouped under /app-screen-mockups and
         marked noindex (see routeMeta.isNoindexRoute) so search engines skip
         them. Not linked from the site nav.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/app-screen-mockups/signup" element={<Signup data-id="element-6" data-mp="3456ioegkm6f5" />} data-id="element-5" data-mp="344ddhhx9foz4" />
        {/* Direct routes onto individual signup screens, so they can be opened
         from the route dropdown without clicking through the flow. */}
        <Route path="/app-screen-mockups/google-consent" element={<Signup data-id="element-8" data-mp="2gej63fvpmsm9" />} data-id="element-7" data-mp="2uetywogkyha2" />
        <Route path="/app-screen-mockups/google-permissions" element={<Signup data-id="element-10" data-mp="2ggn8tuhvhlky" />} data-id="element-9" data-mp="2ge119uuzf62q" />
        <Route path="/app-screen-mockups/giga-chat" element={<Signup data-id="element-12" data-mp="2kf31adwq3m5r" />} data-id="element-11" data-mp="2kge52dq64twv" />
        <Route path="/app-screen-mockups/sign-in" element={<SignIn data-id="element-14" data-mp="30uc8m3yicamc" />} data-id="element-13" data-mp="2khp7oa7vnvme" />
        <Route path="/app-screen-mockups/trial" element={<TrialFlow data-id="element-16" data-mp="2vwk7qkfvxe60" />} data-id="element-15" data-mp="30ulzruvagntm" />
        <Route path="/app-screen-mockups/dashboard" element={<Dashboard data-id="element-18" data-mp="2vug0bsis1x0z" />} data-id="element-17" data-mp="2vxc3pwde9dwt" />
        <Route path="/app-screen-mockups/workflow-show" element={<WorkflowShow data-id="element-20" data-mp="2qzaj370bl6j8" />} data-id="element-19" data-mp="2qwob569iop0h" />
        <Route path="/app-screen-mockups/onboarding-funnel" element={<OnboardingFunnel data-id="element-22" data-mp="2lxux9cqbivzs" />} data-id="element-21" data-mp="2lyd2uzyvemkd" />
        <Route path="/app-screen-mockups/slack-oauth-callback" element={<WorkspaceAlreadyConnected data-id="element-24" data-mp="2hy2op00xcpuf" />} data-id="element-23" data-mp="2hwj632rsq2ph" />
        {/* Figma export surface for the home page's Workspace screens. */}
        <Route path="/app-screen-mockups/export-dashboards" element={<ExportDashboards data-id="element-26" data-mp="287rgh1a7juhq" />} data-id="element-25" data-mp="2d3q0kcoi6qcc" />
        <Route path="/app-screen-mockups/creator-pack" element={<CreatorPack data-id="element-28" data-mp="238eltud3hj4l" />} data-id="element-27" data-mp="284lrrn5hqumj" />

        {/* Waitlist referral capture stays PUBLIC + functional: /r/<code>
         records the inbound referral and forwards to app.getgiga.com. */}
        <Route path="/r/:code" element={<ReferralCapture data-id="element-30" data-mp="1u7zxem4u3nwv" />} data-id="element-29" data-mp="1u76z89jxy6qc" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (standalone, no marketing chrome) — internal review /
         experimental screens. Grouped under /unlisted and marked noindex.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted/sphere-directions" element={<SphereDirections data-id="element-32" data-mp="np747c8dfh64" />} data-id="element-31" data-mp="iqdvry7xygct" />
        <Route path="/unlisted/chat-transition" element={<ChatTransition data-id="element-34" data-mp="3m6kvxur5dvgu" />} data-id="element-33" data-mp="no5k6i9dnkc9" />
        <Route path="/unlisted/use-cases-concepts" element={<UseCasesConcepts data-id="element-36" data-mp="3m4gjupj3hq5h" />} data-id="element-35" data-mp="3m7checr16cte" />
        <Route path="/unlisted/font-options" element={<FontOptions data-id="element-38" data-mp="3r5nrmol8mpc5" />} data-id="element-37" data-mp="3r4uso9sit44k" />
        <Route path="/unlisted/claude-font" element={<ClaudeFont data-id="element-40" data-mp="3w3frc4s4jfqw" />} data-id="element-39" data-mp="3w4h5wj8cmkdd" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (rendered inside the marketing Layout) — home variants,
         unlinked offering pages, and pages parked for later. Same /unlisted
         prefix + noindex, but keep the header/footer chrome so they still
         look like real pages during review.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted" element={<Layout data-id="element-42" data-mp="2k5osgrqkp12a" />} data-id="element-41" data-mp="2f9hlcvmfxjir">
          {/* Home variants / previews */}
          <Route path="architecture" element={<HomeArchitecture data-id="element-44" data-mp="2p51h9i108i7i" />} data-id="element-43" data-mp="2k81d5cnptpj1" />
          <Route path="home-alt" element={<HomeAlternative data-id="element-46" data-mp="2p3gjljje8j5f" />} data-id="element-45" data-mp="2p5uf1thzjxdi" />
          <Route path="ecommerce" element={<EcommerceHome data-id="element-48" data-mp="2u3vzahr5e2qk" />} data-id="element-47" data-mp="2u4nu3qcx7wfs" />
          <Route path="future" element={<FutureHome data-id="element-50" data-mp="1wh5lqgn68wcb" />} data-id="element-49" data-mp="2u21npu1rnmz5" />
          <Route path="agents" element={<Agents data-id="element-52" data-mp="1wej6rvxybek2" />} data-id="element-51" data-mp="1whyjis45kbib" />
          <Route path="workspace" element={<WorkspaceHome data-id="element-54" data-mp="21grzcr6wweln" />} data-id="element-53" data-mp="21fgxwy0xvixp" />
          <Route path="team-workspace" element={<HomeTeamWorkspace data-id="element-56" data-mp="26f3971x7ip3v" />} data-id="element-55" data-mp="21g03df9ekeuu" />
          {/* Offering pages, currently unlinked */}
          <Route path="enterprise" element={<Enterprise data-id="element-58" data-mp="2b5rzidx5hg1f" />} data-id="element-57" data-mp="2b5jmmm112aq8" />
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-60" data-mp="1dq5vzd7urihz" />} data-id="element-59" data-mp="2b7342fyf6rtl" />
          <Route path="bespoke" element={<Bespoke data-id="element-62" data-mp="1dpw2vgrigv82" />} data-id="element-61" data-mp="1dp3535aj5g22" />
          <Route path="consulting" element={<Consulting data-id="element-64" data-mp="1iofo1eya62zd" />} data-id="element-63" data-mp="1ip8zgt86pgnu" />
          <Route path="security" element={<Security data-id="element-66" data-mp="1imwahw40e61a" />} data-id="element-65" data-mp="1ino4x3lvdxpz" />
          {/* Content, parked until ready */}
          <Route path="blog" element={<Blog data-id="element-68" data-mp="3vx5znl22bj9r" />} data-id="element-67" data-mp="3vypjtmquaegt" />
          <Route path="research" element={<Research data-id="element-70" data-mp="3rz8b8kg4917f" />} data-id="element-69" data-mp="3rxdr38co5d4e" />
          <Route path="case-studies" element={<CaseStudies data-id="element-72" data-mp="3mykcfclnbg2n" />} data-id="element-71" data-mp="3rvsvrgij5q5h" />
          <Route path="changelog" element={<Changelog data-id="element-74" data-mp="3n0x2ye5gyyrb" />} data-id="element-73" data-mp="3mxrf128ku2x6" />
          {/* Use cases, parked until ready */}
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-76" data-mp="3d0mpqtjfprc5" />} data-id="element-75" data-mp="3hyxyt21wnxtb" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-78" data-mp="3d2r2m0zba0ok" />} data-id="element-77" data-mp="3d0wdeah0bmeo" />
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-80" data-mp="388mtbb2kpgkb" />} data-id="element-79" data-mp="389ef5u6dbzxe" />
          {/* Comparison pages, parked until ready */}
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-82" data-mp="2z984sh5yvg2g" />} data-id="element-81" data-mp="348uhmphvuuaq" />
        </Route>

        {/* ──────────────────────────────────────────────────────────────
         PUBLIC marketing site — indexable.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/" element={<Layout data-id="element-84" data-mp="2pblkff5wewb3" />} data-id="element-83" data-mp="2pc3hge0dxek2">
          {/* The four-pillar platform page is the home page. The previous
           Tasklet-style home now lives at /ai-coworker. */}
          <Route index element={<Alt data-id="element-86" data-mp="2knc5exg64u58" />} data-id="element-85" data-mp="2kn2gzearuv1n" />
          <Route path="ai-coworker" element={<Home data-id="element-88" data-mp="26mrok6ttd8ed" />} data-id="element-87" data-mp="2knu9uhczieo8" />
          {/* The previous home page, kept reachable now that the ported page
           owns "/". Same component as /ai-coworker. */}
          <Route path="home-old" element={<Home data-id="element-90" data-mp="2aq6d5lvtix3f" />} data-id="element-89" data-mp="2aov9rn6abrcu" />
          <Route path="terms" element={<Terms data-id="element-92" data-mp="2yez5z9h58ott" />} data-id="element-91" data-mp="2ydflf6ogfrm8" />
          <Route path="affiliate-terms-reditus" element={<AffiliateTerms data-id="element-94" data-mp="33dk3v28xawf3" />} data-id="element-93" data-mp="2yepm83mozdwk" />
          <Route path="privacy" element={<Privacy data-id="element-96" data-mp="33f4x8oji49bd" />} data-id="element-95" data-mp="33bpijmtqp2ah" />
          <Route path="vulnerability-disclosure" element={<VulnerabilityDisclosure data-id="element-98" data-mp="2pkudymq9x7cp" />} data-id="element-97" data-mp="2plvsx2aeudzp" />
          <Route path="contact" element={<Contact data-id="element-100" data-mp="2tmfpo4r2nflb" />} data-id="element-99" data-mp="2tn7m1hshthcn" />
          <Route path="support" element={<Support data-id="element-102" data-mp="2tl4p0dsxanyf" />} data-id="element-101" data-mp="2tlwgpdjeg1jf" />
          <Route path="developers" element={<Developers data-id="element-104" data-mp="3hbqr7og8xe0i" />} data-id="element-103" data-mp="3hd1w5rlfgrt7" />
          <Route path="developers/api" element={<Developers data-id="element-106" data-mp="3m39z1qgllk7j" />} data-id="element-105" data-mp="3hbih24bicmt0" />
          <Route path="developers/openapi" element={<Developers data-id="element-108" data-mp="3m5mpks0f92w7" />} data-id="element-107" data-mp="3m4jxigrd4uep" />
          <Route path="developers/authentication" element={<Developers data-id="element-110" data-mp="1amyn30p0xhx9" />} data-id="element-109" data-mp="1ao9qv0igypod" />
          <Route path="developers/webhooks" element={<Developers data-id="element-112" data-mp="15q85ua1hgga9" />} data-id="element-111" data-mp="1am6odh04r42r" />
          <Route path="developers/mcp" element={<Developers data-id="element-114" data-mp="1kecqhjk3j1vo" />} data-id="element-113" data-mp="15ox22a81f8j5" />
          <Route path="new" element={<NewInfrastructure data-id="element-116" data-mp="1kd1fayobmruj" />} data-id="element-115" data-mp="1kem5yd7ilqn4" />
          {/* /alt was this page's home before the swap. It renders the page
           directly rather than redirecting to "/": a Navigate drops the query
           string, so any preview or campaign link carrying params landed on a
           blank frame instead of the page. */}
          <Route path="alt" element={<Alt data-id="element-118" data-mp="11nv2tte4ewh6" />} data-id="element-117" data-mp="msf61gm5omh3" />
          {/* Enterprise positioning variant. Lives at a top-level path so it is
           easy to open and preview directly; kept out of the index via
           NOINDEX_ROUTES. */}
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-120" data-mp="2d1p0yvczfj8m" />} data-id="element-119" data-mp="2d2rsn5i4pppl" />
          <Route path="careers" element={<Careers data-id="element-122" data-mp="284ghmrg3l95s" />} data-id="element-121" data-mp="2d4ussiczwz81" />
        <Route path="pricing" element={<Pricing data-id="element-124" data-mp="283nm6mmla62y" />} data-id="element-123" data-mp="282vpt9l644bm" />
          <Route path="giga-for-chatgpt" element={<GigaForChatGPT data-id="element-126" data-mp="hnv3um3w3rcd" />} data-id="element-125" data-mp="hmtfjfxv58cp" />
          <Route path="chatgpt" element={<GigaForChatGPT2 data-id="element-128" data-mp="mlwmx71bfqnu" />} data-id="element-127" data-mp="hoo02t582yg9" />
          <Route path="company-brain" element={<CompanyBrain data-id="element-130" data-mp="mm69ekn5jfos" />} data-id="element-129" data-mp="mmxwf72oo53g" />
          <Route path="giga-for-chatgpt-2" element={<Navigate to="/chatgpt" replace data-id="element-132" data-mp="qmq8htractdk" />} data-id="element-131" data-mp="qnhwojijzott" />
          <Route path="creator-program" element={<CreatorProgram data-id="element-134" data-mp="vmuolioa7m39" />} data-id="element-133" data-mp="vmkzryez3kz5" />
          <Route path="viktor-alternative" element={<ViktorAlternative data-id="element-136" data-mp="10lxjwbegmdy1" />} data-id="element-135" data-mp="vnw2duwommoo" />
          <Route path="integrations" element={<Integrations data-id="element-138" data-mp="10jl5g87wulpq" />} data-id="element-137" data-mp="10kw9m959pvhd" />
          {/* Data-driven per-integration pages, sourced from the Giga catalog
            (single source of truth). Replaces the old one-off pages. */}
          <Route path="integrations/:slug" element={<IntegrationDetail data-id="element-140" data-mp="1ecucw3casx5m" />} data-id="element-139" data-mp="1acafqzrq5tjh" />
          <Route path="features/:slug" element={<FeatureDetail data-id="element-142" data-mp="1ef6ozzvdkdar" />} data-id="element-141" data-mp="1ec14kxxoxzld" />
          {/* AI Agents has a bespoke page rather than the shared feature
            template. Static segments outrank the dynamic one in the router, so
            this wins over `features/:slug` regardless of order here. */}
          <Route path="features/ai-agents" element={<AIAgents data-id="element-144" data-mp="1yd42y9g0xvo1" />} data-id="element-143" data-mp="1yg89hdgyp3hv" />
          {/* ARCHIVE, deliberately unlinked. The template rendering of the same
            feature, kept reachable so nothing is lost while the new page is
            being judged. */}
          <Route path="features/ai-agents/alt" element={<FeatureDetail slugOverride="ai-agents" data-id="element-146" data-mp="1kgh05q1e4ba7" />} data-id="element-145" data-mp="1kfffquyhicft" />
          {/* The feature was called Assistants. Existing links must not 404. */}
          <Route path="features/assistants" element={<Navigate to="/features/ai-agents" replace data-id="element-148" data-mp="2c6brfs8t6b1i" />} data-id="element-147" data-mp="2h7sunuerm9l9" />
          <Route path="features/connect-to-slack" element={<Navigate to="/features/giga-in-slack" replace data-id="element-150" data-mp="27etqh46scagj" />} data-id="element-149" data-mp="2c8xy7pnvkmio" />
          <Route path="features/integrate-with-databases" element={<Navigate to="/features/connect-to-databases" replace data-id="element-152" data-mp="23ds1rhahvgst" />} data-id="element-151" data-mp="23e9sjyee12tc" />
          <Route path="features/backed-by-a-full-computer" element={<Navigate to="/features/giga-cloud-computer" replace data-id="element-154" data-mp="2zy0pzqc83vqw" />} data-id="element-153" data-mp="2zwfu9xe6a6rg" />
          <Route path="features/giga-cloud-computer" element={<Navigate to="/features/a-full-computer" replace data-id="element-156" data-mp="t8764kt1iozl" />} data-id="element-155" data-mp="2v3whv3r81ho8" />
          <Route path="features/browser-control" element={<Navigate to="/features/cloud-browser" replace data-id="element-158" data-mp="y7jq8xsj1hyh" />} data-id="element-157" data-mp="t8p7fvuk7ted" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-160" data-mp="jidx9rka8nz1" />} data-id="element-159" data-mp="y8uueypvwrq4" />
          {/* The page is a sales enquiry form, not a booking calendar, so the
           URL says so. /demo/book is kept as a permanent redirect: it has been
           shared in emails and linked from other pages, and a dead link on a
           sales CTA is the most expensive kind. */}
          <Route path="talk-to-sales" element={<BookDemo data-id="element-162" data-mp="fhtyyknz3ebb" />} data-id="element-161" data-mp="fhk68pbjmt1x" />
          <Route path="demo/book" element={<Navigate to="/talk-to-sales" replace data-id="element-164" data-mp="k1lrz5wf20a" />} data-id="element-163" data-mp="j9omjwnkw7w" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}